import { useState, useMemo } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Plus, Pencil, Trash2, X, Loader2, Palette, Tag } from 'lucide-react'
import toast from 'react-hot-toast'
import { api } from '@/lib/api'
import { pedirExclusao } from '@/lib/confirmarExclusao'

interface GradeTipo {
  id: number
  codigo: string
  label: string
  unidade_label: string | null
  tipo_dado: 'texto' | 'numero' | 'dimensao'
  define_receita: boolean
  ordem: number
  ativo: boolean
}
interface GradeValor {
  id: number
  tipo_id: number
  codigo: string
  label: string
  valor_numerico: number | null
  hex_cor: string | null
  ordem: number
  ativo: boolean
}

const TIPO_DADO_OPTS = [
  { v: 'texto', t: 'Texto' },
  { v: 'numero', t: 'Numérico' },
  { v: 'dimensao', t: 'Dimensão' },
]

const EMPTY_TIPO: Partial<GradeTipo> = {
  codigo: '', label: '', unidade_label: null,
  tipo_dado: 'texto', define_receita: false, ordem: 0, ativo: true,
}
const EMPTY_VALOR: Partial<GradeValor> = {
  codigo: '', label: '', valor_numerico: null, hex_cor: null, ordem: 0, ativo: true,
}

export default function GradesPage() {
  const qc = useQueryClient()
  const [tipoSel, setTipoSel] = useState<number | null>(null)
  const [editTipo, setEditTipo] = useState<Partial<GradeTipo> | null>(null)
  const [editValor, setEditValor] = useState<Partial<GradeValor> | null>(null)

  const { data: tipos = [], isLoading: lt } = useQuery<GradeTipo[]>({
    queryKey: ['grade-tipos'],
    queryFn: async () => (await api.get('/cadastros/grade-tipos')).data,
  })

  const { data: valores = [], isLoading: lv } = useQuery<GradeValor[]>({
    queryKey: ['grade-valores', tipoSel],
    queryFn: async () => {
      if (!tipoSel) return []
      return (await api.get('/cadastros/grade-valores', { params: { tipo_id: tipoSel, apenas_ativos: false } })).data
    },
    enabled: !!tipoSel,
  })

  const tipoAtual = useMemo(() => tipos.find(t => t.id === tipoSel), [tipos, tipoSel])

  const salvarTipo = useMutation({
    mutationFn: async (d: Partial<GradeTipo>) => {
      const payload = {
        codigo: d.codigo, label: d.label, unidade_label: d.unidade_label || null,
        tipo_dado: d.tipo_dado, define_receita: !!d.define_receita,
        ordem: d.ordem ?? 0, ativo: d.ativo ?? true,
      }
      if (d.id) return (await api.put(`/cadastros/grade-tipos/${d.id}`, payload)).data
      return (await api.post('/cadastros/grade-tipos', payload)).data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['grade-tipos'] })
      setEditTipo(null); toast.success('Salvo')
    },
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'Erro ao salvar'),
  })

  const removerTipo = useMutation({
    mutationFn: async (id: number) => (await api.delete(`/cadastros/grade-tipos/${id}`)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['grade-tipos'] })
      if (tipoSel) qc.invalidateQueries({ queryKey: ['grade-valores', tipoSel] })
      setTipoSel(null); toast.success('Removido')
    },
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'Erro ao remover'),
  })

  const salvarValor = useMutation({
    mutationFn: async (d: Partial<GradeValor>) => {
      const payload = {
        tipo_id: d.tipo_id, codigo: d.codigo, label: d.label,
        valor_numerico: d.valor_numerico ?? null,
        hex_cor: d.hex_cor || null,
        ordem: d.ordem ?? 0, ativo: d.ativo ?? true,
      }
      if (d.id) return (await api.put(`/cadastros/grade-valores/${d.id}`, payload)).data
      return (await api.post('/cadastros/grade-valores', payload)).data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['grade-valores', tipoSel] })
      setEditValor(null); toast.success('Salvo')
    },
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'Erro ao salvar'),
  })

  const removerValor = useMutation({
    mutationFn: async (id: number) => (await api.delete(`/cadastros/grade-valores/${id}`)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['grade-valores', tipoSel] })
      toast.success('Removido')
    },
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'Erro ao remover'),
  })

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-ink-900 flex items-center gap-2">
            <Tag size={20} className="text-brand-600" /> Grades de Produto
          </h2>
          <p className="text-sm text-ink-500">Atributos como cor, espessura, milimetragem — usados em produtos e fichas técnicas</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* COLUNA TIPOS */}
        <div className="card p-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-semibold">Tipos</h3>
            <button className="btn-primary text-sm" onClick={() => setEditTipo({ ...EMPTY_TIPO })}>
              <Plus size={14} className="inline" /> Novo tipo
            </button>
          </div>
          {lt ? (
            <div className="text-center py-8"><Loader2 className="animate-spin inline" /></div>
          ) : tipos.length === 0 ? (
            <div className="text-sm text-ink-500 py-8 text-center">Nenhum tipo cadastrado</div>
          ) : (
            <div className="space-y-1">
              {tipos.map(t => (
                <div key={t.id}
                  className={`flex items-center justify-between gap-2 px-3 py-2 rounded cursor-pointer ${tipoSel === t.id ? 'bg-brand-50 border border-brand-200' : 'hover:bg-ink-50'}`}
                  onClick={() => setTipoSel(t.id)}>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-ink-900 flex items-center gap-2">
                      {t.label}
                      {t.define_receita && <span className="text-[10px] bg-purple-100 text-purple-700 px-1.5 py-0.5 rounded">define receita</span>}
                      {!t.ativo && <span className="text-[10px] bg-ink-100 text-ink-600 px-1.5 py-0.5 rounded">inativo</span>}
                    </div>
                    <div className="text-xs text-ink-500">{t.codigo} · {t.tipo_dado}{t.unidade_label ? ` · ${t.unidade_label}` : ''}</div>
                  </div>
                  <div className="flex gap-1">
                    <button className="text-ink-500 hover:text-brand-600 p-1" onClick={(e) => { e.stopPropagation(); setEditTipo(t) }}>
                      <Pencil size={14} />
                    </button>
                    <button className="text-ink-500 hover:text-red-600 p-1" onClick={(e) => { e.stopPropagation(); pedirExclusao({ descricao: `o tipo "${t.label}"`, onConfirm: () => removerTipo.mutate(t.id) }) }}>
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* COLUNA VALORES */}
        <div className="card p-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-semibold">{tipoAtual ? `Valores de ${tipoAtual.label}` : 'Selecione um tipo'}</h3>
            {tipoAtual && (
              <button className="btn-primary text-sm" onClick={() => setEditValor({ ...EMPTY_VALOR, tipo_id: tipoAtual.id })}>
                <Plus size={14} className="inline" /> Novo valor
              </button>
            )}
          </div>
          {!tipoSel ? (
            <div className="text-sm text-ink-400 py-12 text-center">Selecione um tipo na coluna ao lado</div>
          ) : lv ? (
            <div className="text-center py-8"><Loader2 className="animate-spin inline" /></div>
          ) : valores.length === 0 ? (
            <div className="text-sm text-ink-500 py-8 text-center">Nenhum valor cadastrado</div>
          ) : (
            <div className="space-y-1">
              {valores.map(v => (
                <div key={v.id} className="flex items-center justify-between gap-2 px-3 py-2 rounded hover:bg-ink-50">
                  <div className="flex items-center gap-2 flex-1 min-w-0">
                    {v.hex_cor && (
                      <div className="w-5 h-5 rounded border border-ink-200 flex-shrink-0" style={{ background: v.hex_cor }} />
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-ink-900 flex items-center gap-2">
                        {v.label}
                        {!v.ativo && <span className="text-[10px] bg-ink-100 text-ink-600 px-1.5 py-0.5 rounded">inativo</span>}
                      </div>
                      <div className="text-xs text-ink-500">
                        {v.codigo}
                        {v.valor_numerico != null && ` · ${v.valor_numerico}${tipoAtual?.unidade_label || ''}`}
                      </div>
                    </div>
                  </div>
                  <div className="flex gap-1">
                    <button className="text-ink-500 hover:text-brand-600 p-1" onClick={() => setEditValor(v)}>
                      <Pencil size={14} />
                    </button>
                    <button className="text-ink-500 hover:text-red-600 p-1" onClick={() => pedirExclusao({ descricao: `o valor "${v.label}"`, onConfirm: () => removerValor.mutate(v.id) })}>
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* MODAL TIPO */}
      {editTipo && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => setEditTipo(null)}>
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full" onClick={e => e.stopPropagation()}>
            <div className="p-4 border-b flex items-center justify-between">
              <h3 className="font-semibold">{editTipo.id ? 'Editar tipo' : 'Novo tipo de grade'}</h3>
              <button onClick={() => setEditTipo(null)}><X size={18} /></button>
            </div>
            <div className="p-4 space-y-3">
              <div>
                <label className="text-xs text-ink-600">Código (UPPER)</label>
                <input className="input w-full" value={editTipo.codigo || ''} onChange={e => setEditTipo({ ...editTipo, codigo: e.target.value.toUpperCase() })} placeholder="COR, ESPESSURA, MM..." />
              </div>
              <div>
                <label className="text-xs text-ink-600">Rótulo</label>
                <input className="input w-full" value={editTipo.label || ''} onChange={e => setEditTipo({ ...editTipo, label: e.target.value })} placeholder="Cor, Espessura..." />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-ink-600">Tipo do dado</label>
                  <select className="input w-full" value={editTipo.tipo_dado || 'texto'} onChange={e => setEditTipo({ ...editTipo, tipo_dado: e.target.value as any })}>
                    {TIPO_DADO_OPTS.map(o => <option key={o.v} value={o.v}>{o.t}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-xs text-ink-600">Unidade (opcional)</label>
                  <input className="input w-full" value={editTipo.unidade_label || ''} onChange={e => setEditTipo({ ...editTipo, unidade_label: e.target.value })} placeholder="mm, °, KG..." />
                </div>
              </div>
              <div className="flex items-center gap-3 pt-1">
                <label className="flex items-center gap-2 text-sm">
                  <input type="checkbox" checked={!!editTipo.define_receita} onChange={e => setEditTipo({ ...editTipo, define_receita: e.target.checked })} />
                  Define receita (auto-OP usa pra escolher composição)
                </label>
              </div>
              <div className="flex items-center gap-3">
                <label className="flex items-center gap-2 text-sm">
                  <input type="checkbox" checked={editTipo.ativo !== false} onChange={e => setEditTipo({ ...editTipo, ativo: e.target.checked })} />
                  Ativo
                </label>
              </div>
            </div>
            <div className="p-4 border-t flex justify-end gap-2">
              <button className="btn-ghost" onClick={() => setEditTipo(null)}>Cancelar</button>
              <button className="btn-primary" onClick={() => salvarTipo.mutate(editTipo)} disabled={salvarTipo.isPending}>
                {salvarTipo.isPending ? <Loader2 className="animate-spin inline" size={14} /> : 'Salvar'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL VALOR */}
      {editValor && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => setEditValor(null)}>
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full" onClick={e => e.stopPropagation()}>
            <div className="p-4 border-b flex items-center justify-between">
              <h3 className="font-semibold">{editValor.id ? 'Editar valor' : 'Novo valor'}</h3>
              <button onClick={() => setEditValor(null)}><X size={18} /></button>
            </div>
            <div className="p-4 space-y-3">
              <div>
                <label className="text-xs text-ink-600">Código (UPPER)</label>
                <input className="input w-full" value={editValor.codigo || ''} onChange={e => setEditValor({ ...editValor, codigo: e.target.value.toUpperCase() })} placeholder="PRETO, 3MM..." />
              </div>
              <div>
                <label className="text-xs text-ink-600">Rótulo</label>
                <input className="input w-full" value={editValor.label || ''} onChange={e => setEditValor({ ...editValor, label: e.target.value })} placeholder="Preto, 3 mm..." />
              </div>
              {tipoAtual?.tipo_dado !== 'texto' && (
                <div>
                  <label className="text-xs text-ink-600">Valor numérico {tipoAtual?.unidade_label ? `(${tipoAtual.unidade_label})` : ''}</label>
                  <input type="number" step="0.001" className="input w-full"
                    value={editValor.valor_numerico ?? ''}
                    onChange={e => setEditValor({ ...editValor, valor_numerico: e.target.value === '' ? null : Number(e.target.value) })} />
                </div>
              )}
              {tipoAtual?.codigo === 'COR' && (
                <div>
                  <label className="text-xs text-ink-600 flex items-center gap-1"><Palette size={12} /> Cor (hex)</label>
                  <div className="flex gap-2 items-center">
                    <input type="color" className="h-9 w-14 rounded border" value={editValor.hex_cor || '#000000'} onChange={e => setEditValor({ ...editValor, hex_cor: e.target.value })} />
                    <input className="input flex-1" value={editValor.hex_cor || ''} onChange={e => setEditValor({ ...editValor, hex_cor: e.target.value })} placeholder="#000000" />
                  </div>
                </div>
              )}
              <div className="flex items-center gap-3">
                <label className="flex items-center gap-2 text-sm">
                  <input type="checkbox" checked={editValor.ativo !== false} onChange={e => setEditValor({ ...editValor, ativo: e.target.checked })} />
                  Ativo
                </label>
              </div>
            </div>
            <div className="p-4 border-t flex justify-end gap-2">
              <button className="btn-ghost" onClick={() => setEditValor(null)}>Cancelar</button>
              <button className="btn-primary" onClick={() => salvarValor.mutate(editValor)} disabled={salvarValor.isPending}>
                {salvarValor.isPending ? <Loader2 className="animate-spin inline" size={14} /> : 'Salvar'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
